"use client";

import { createContext, ReactNode, useContext, useState } from "react";

interface PopupContextType {
  isOpen: boolean;
  title: string;
  content: ReactNode | null;
  maxWidth?: string;
  openPopup: (title: string, content: ReactNode, maxWidth?: string) => void;
  closePopup: () => void;
}

const PopupContext = createContext<PopupContextType>({
  isOpen: false,
  title: "",
  content: null,
  maxWidth: undefined,
  openPopup: () => {},
  closePopup: () => {},
});

export function PopupProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState<string>("");
  const [content, setContent] = useState<ReactNode | null>(null);
  const [maxWidth, setMaxWidth] = useState<string | undefined>(undefined);

  const openPopup = (title: string, content: ReactNode, maxWidth?: string) => {
    setTitle(title);
    setContent(content);
    setIsOpen(true);
    setMaxWidth(maxWidth);
  };

  const closePopup = () => {
    setIsOpen(false);
    setContent(null);
    setTitle("");
    setMaxWidth(undefined);
  };

  return (
    <PopupContext.Provider
      value={{ isOpen, title, content, maxWidth, openPopup, closePopup }}
    >
      {children}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div
            className="flex flex-col bg-white rounded shadow-lg relative border border-[#283046]"
            style={{ maxWidth: maxWidth || undefined }}
          >
            <div className="flex justify-between bg-[#283046] py-2 px-5 text-white ">
              <span className=" text-lg font-bold">{title}</span>
              <button className="cursor-pointer" onClick={closePopup}>
                ✕
              </button>
            </div>
            <div className="flex flex-col px-2.5 py-5">{content}</div>
          </div>
        </div>
      )}
    </PopupContext.Provider>
  );
}

export function usePopup() {
  return useContext(PopupContext);
}
